import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COUNTRIES,
  countryMatchesSearch,
  getCountryDisplayName,
  getCountryLabel,
} from '../../data/countries';

interface CountryAutocompleteProps {
  name: string;
  value: string;
  onChange: (name: string, value: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  noResultsText?: string;
}

interface Country {
  name: string;
  alpha2: string;
}

export const CountryAutocomplete: React.FC<CountryAutocompleteProps> = ({
  name,
  value,
  onChange,
  placeholder = '',
  required = false,
  className = '',
  noResultsText = '',
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const [searchValue, setSearchValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const findExactCountry = (search: string): Country | undefined => {
    const normalizedSearch = search.trim().toLowerCase();

    return COUNTRIES.find((country) => (
      country.name.toLowerCase() === normalizedSearch ||
      country.alpha2.toLowerCase() === normalizedSearch ||
      getCountryDisplayName(country, language).toLowerCase() === normalizedSearch ||
      getCountryLabel(country, language).toLowerCase() === normalizedSearch
    ));
  };
  const selectedCountry = value ? COUNTRIES.find(c => c.alpha2 === value) : null;
  const selectedLabel = selectedCountry ? getCountryLabel(selectedCountry, language) : '';
  const displayValue = isEditing ? searchValue : selectedLabel;

  // Filter countries based on input, matching the displayed (localized) name
  // as well as the canonical name so search follows the active language.
  const filterCountries = (search: string): Country[] => {
    if (!search.trim()) {
      return COUNTRIES;
    }

    return COUNTRIES.filter(country => countryMatchesSearch(country, search, language));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    setFilteredCountries(filterCountries(newValue));
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleSelectCountry = (country: Country) => {
    setSearchValue('');
    onChange(name, country.alpha2);
    setIsOpen(false);
    setHighlightedIndex(-1);
    setIsEditing(false);
    inputRef.current?.blur();
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    const currentSearch = searchValue.trim();
    setFilteredCountries(filterCountries(currentSearch));
    setIsOpen(true);
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    // Delay closing to allow click on dropdown items
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        const exactCountry = findExactCountry(searchValue);

        if (exactCountry) {
          handleSelectCountry(exactCountry);
          return;
        }

        setIsOpen(false);
        setIsEditing(false);
        setSearchValue('');
      }
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setFilteredCountries(filterCountries(searchValue));
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredCountries.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
          handleSelectCountry(filteredCountries[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        className={className}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={`${name}-listbox`}
        aria-haspopup="listbox"
        aria-autocomplete="list"
      />
      
      {/* Hidden input to store the actual value for form validation */}
      <input
        type="hidden"
        name={name}
        value={value}
      />

      {isOpen && filteredCountries.length > 0 && (
        <ul
          ref={listRef}
          id={`${name}-listbox`}
          className="absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
          role="listbox"
        >
          {filteredCountries.map((country, index) => (
            <li
              key={country.alpha2}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelectCountry(country);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 text-sm cursor-pointer ${
                index === highlightedIndex
                  ? 'bg-slate-100 text-slate-900'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
              role="option"
              aria-selected={index === highlightedIndex}
            >
              {getCountryLabel(country, language)}
            </li>
          ))}
        </ul>
      )}

      {isOpen && filteredCountries.length === 0 && searchValue.trim() && (
        <div className="absolute z-50 w-full mt-1 px-3 py-2 text-sm text-slate-500 rounded-md border border-slate-200 bg-white shadow-lg">
          {noResultsText}
        </div>
      )}
    </div>
  );
};
